import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams, Link } from "react-router-dom";
import { ArrowUpLongIcon, Body2, Button, InfoBox, SpinnerLoader } from "@activecollab/components";
import ExternalEmptySlate from "~/components/EmptySlate/ExternalEmptySlate";
import PublicLayout from "~/pages/Layouts/PublicLayout";
import { usePublicPayments } from "~/repository/usePublicPayments";
import localizationService from "~/services/LocalizationService";
import { resolvePublicInvoiceLink } from "~/utils/utils";

export const PublicPaymentCreditCardCompleted = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [first, setFirst] = useState(false);
  const [showEmptySlate, setShowEmptySlate] = useState(false);
  const { data, isSuccess, isFetched, isError } = usePublicPayments(
    searchParams.get("number"),
    searchParams.get("hash"),
    { enabled: !!searchParams.get("number") && !!searchParams.get("hash") }
  );
  useEffect(() => {
    if (!searchParams.get("number") || !searchParams.get("hash")) {
      return setShowEmptySlate(true);
    }

    if (location.state?.first) {
      setFirst(true);
      navigate(location.pathname + `?number=${searchParams.get("number")}&hash=${searchParams.get("hash")}`, {
        replace: true,
        state: { ...location.state, first: null },
      });
    }
  }, [location.pathname, location.state, navigate, searchParams]);
  if (data?.language) {
    localizationService.configure({ language_id: data?.language?.id }, [data?.language], true);
  }

  if (showEmptySlate) {
    return (
      <PublicLayout
        title={t("Not Found")}
        shouldShowTerms={false}
      >
        <ExternalEmptySlate isLoggedIn={false} />
      </PublicLayout>
    );
  }

  return isFetched ? (
    <PublicLayout
      shouldShowLogo
      paperClassName="tw-flex tw-flex-col tw-items-center tw-p-8 tw-w-[360px]"
      bodyClassName="tw-flex tw-items-center tw-justify-center"
    >
      {isSuccess && first && data?.payment?.status === "paid" ? (
        <>
          <InfoBox
            type="success"
            title={t("Payment Completed!")}
            showIcon
          >
            <Body2>
              {t("Your payment of {{amount}} {{currencyCode}} has been successfully completed.", {
                amount: data?.payment?.amount,
                currencyCode: data?.currency_code,
              })}
              {data?.payment?.created_by?.email ? (
                <>
                  <br />
                  <br />
                  {t("We'll send a confirmation email to: {{email}}.", { email: data?.payment?.created_by?.email })}
                </>
              ) : null}
            </Body2>
          </InfoBox>
          <Link to={resolvePublicInvoiceLink(data?.back_url)}>
            <Button
              variant="text colored"
              className="tw-mt-6"
            >
              {t("Continue To Invoice")}
              <ArrowUpLongIcon className="tw-rotate-90" />
            </Button>
          </Link>
        </>
      ) : null}
      {isSuccess && !first && data?.payment?.status === "paid" ? (
        <>
          <InfoBox
            type="success"
            title={t("Payment Completed!")}
            showIcon
          >
            <Body2>{t("This invoice has already been paid.")}</Body2>
          </InfoBox>
          <Link to={resolvePublicInvoiceLink(data?.back_url)}>
            <Button
              variant="text colored"
              className="tw-mt-6"
            >
              {t("Continue To Invoice")}
              <ArrowUpLongIcon className="tw-rotate-90" />
            </Button>
          </Link>
        </>
      ) : null}
      {isError ? (
        <>
          <InfoBox
            type="note"
            title={t("Payment Status Information Error")}
            showIcon
          >
            <Body2>
              {t("There was an error while retrieving payment status.")}
              <br />
              <br />
              {t("Please check your payment, by opening the provided link.")}
            </Body2>
          </InfoBox>
          {data?.back_url ? (
            <Link to={resolvePublicInvoiceLink(data?.back_url)}>
              <Button
                variant="text colored"
                className="tw-mt-6"
              >
                <ArrowUpLongIcon className="-tw-rotate-90" />
                {t("Back To Invoice")}
              </Button>
            </Link>
          ) : null}
        </>
      ) : null}
    </PublicLayout>
  ) : (
    <PublicLayout
      shouldShowLogo
      paperClassName="tw-flex tw-flex-col tw-items-center tw-p-8 tw-w-[360px]"
      bodyClassName="tw-flex tw-items-center tw-justify-center"
    >
      <SpinnerLoader
        radius={12}
        stroke={2}
        data-testid="spinner-loader"
      />
    </PublicLayout>
  );
};

export default PublicPaymentCreditCardCompleted;
